import React from "react";
import "./GroupCard.css";
import {Accordion, ListGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLayerGroup, faCode} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import {tagColor, deviceTypeBadgeColor, getBondColor} from "../../../../../../lib/ui/colorUtils";
import {padDeviceId} from "../../../../../../lib/utils";
import ReactJson from "react-json-view";
import { useTimezone } from "../../../../contexts/TimezoneContext";

function GroupCard({ groups = [], devices = [] }) {
    const { formatDate, getRelativeTime } = useTimezone();

    function getDeviceInfo(deviceId) {
        const paddedSearchId = padDeviceId(deviceId);
        const device = devices.find(d => {
            const dId = d.device_id || d.id;
            const paddedDId = padDeviceId(dId);
            return dId === deviceId || dId === paddedSearchId ||
                   paddedDId === deviceId || paddedDId === paddedSearchId ||
                   d.id.endsWith(`:${deviceId}`) || d.id.endsWith(`:${paddedSearchId}`);
        });
        return device;
    }

    function renderGroupProperties(group) {
        const infoKeys = ["state", "properties"];
        return (<Accordion className={"mt-2"}>
            {infoKeys.map((key, index) => group[key] ? (<Accordion.Item eventKey={index.toString()} key={key}>
                <Accordion.Header>{key.charAt(0).toUpperCase() + key.slice(1)}</Accordion.Header>
                <Accordion.Body className={"p-3"}>
                    {typeof group[key] === "object" && !Array.isArray(group[key]) ? (Object.entries(group[key]).sort(([a], [b]) => {
                        if (a.startsWith("_") && !b.startsWith("_")) return 1;
                        if (!a.startsWith("_") && b.startsWith("_")) return -1;
                        return a.localeCompare(b);
                    }).map(([subKey, subValue]) => (
                        <FloatingLabel label={subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                                       key={subKey}>
                            <Form.Control readOnly={true} value={subValue ?? ""}/>
                        </FloatingLabel>))) : (<FloatingLabel label={key.charAt(0).toUpperCase() + key.slice(1)}>
                        <Form.Control readOnly={true} value={group[key] ?? ""}/>
                    </FloatingLabel>)}
                </Accordion.Body>
            </Accordion.Item>) : null)}
        </Accordion>);
    }


    if (!groups || groups.length === 0) {
        return null;
    }

    return (<Accordion defaultActiveKey="0" className="mb-2">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                Groups
                            </div>
                            <Badge bg="dark">
                                {groups.length}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-2">
                        <Accordion alwaysOpen>
                            {groups.map((group, index) => {
                                const groupId = group.id || group.group_id || index;
                                return <Accordion.Item eventKey={`${groupId}-group`}
                                                       key={`${groupId}-group`}>
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                                            <Badge bg={"success"} className="flex-shrink-0">
                                                <FontAwesomeIcon icon={faLayerGroup} className={"me-1"}/> Group
                                            </Badge>
                                            <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                {group.name || `Group ${index + 1}`}
                                            </span>
                                            <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                                {group.bond_ids ? (
                                                    group.bond_ids.map((bondId, bondIdx) => (
                                                        <Badge key={bondIdx} className="bondBadge"
                                                               style={{'--bond-badge-bg': getBondColor(bondId)}}>{bondId}
                                                        </Badge>
                                                    ))
                                                ) : group.bond_id ? (
                                                    <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(group.bond_id)}}>{group.bond_id}
                                                    </Badge>
                                                ) : null}
                                                {group.devices && group.devices.length > 0 && (
                                                    <Badge bg={"dark"}>
                                                        {group.devices.length} devices
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </Accordion.Header>

                                    <Accordion.Body className={"p-3"}>
                                        <FloatingLabel label="Name" className="mb-2">
                                            <Form.Control readOnly={true} value={group.name || ""}/>
                                        </FloatingLabel>
                                        {group.group_id && (
                                            <FloatingLabel label="Group ID" className="mb-2">
                                                <Form.Control readOnly={true} value={group.group_id}/>
                                            </FloatingLabel>
                                        )}

                                        <div className="d-flex gap-2 mb-2">
                                            {group.created && (
                                                <FloatingLabel label={`Created ${getRelativeTime(group.created)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(group.created)}/>
                                                </FloatingLabel>
                                            )}
                                            {group.modified && (
                                                <FloatingLabel label={`Modified ${getRelativeTime(group.modified)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(group.modified)}/>
                                                </FloatingLabel>
                                            )}
                                        </div>

                                        {/* Devices List with Badges */}
                                        {group.devices && group.devices.length > 0 && (
                                            <div className="mb-3">
                                                <h6>Devices</h6>
                                                <ListGroup>
                                                    {group.devices.map((deviceId, idx) => {
                                                        const device = getDeviceInfo(deviceId);
                                                        if (!device) {
                                                            return (
                                                                <ListGroup.Item key={idx}>
                                                                    <Badge bg="info">{deviceId}</Badge>
                                                                </ListGroup.Item>
                                                            );
                                                        }
                                                        return (
                                                            <ListGroup.Item key={idx}>
                                                                <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                                                                    <Badge className="flex-shrink-0" style={deviceTypeBadgeColor(device.device_type || device.type)}>
                                                                        {device.device_type || device.type || "Device"}
                                                                    </Badge>
                                                                    <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                                        {device.name}
                                                                    </span>
                                                                    <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                                                        {(device.properties?.branding_profile || device.properties?.pairing_assets_key) && (
                                                                            <Badge bg={"dark"}>
                                                                                {device.properties.branding_profile || device.properties.pairing_assets_key}
                                                                            </Badge>
                                                                        )}
                                                                        {device.bond_id && (
                                                                            <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(device.bond_id)}}>{device.bond_id}
                                                                            </Badge>
                                                                        )}
                                                                        {device.template_id ? (
                                                                            <Badge bg={"info"}
                                                                                   style={tagColor("template", device.template_id)}>{device.template_id}
                                                                            </Badge>
                                                                        ) : device.template ? (
                                                                            <Badge bg={"info"}
                                                                                   style={tagColor("template", device.template)}>{device.template}
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge bg={"warning"}>
                                                                                RAW
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        );
                                                    })}
                                                </ListGroup>
                                            </div>
                                        )}

                                        {/* Actions Section */}
                                        {group.actions && Array.isArray(group.actions) && group.actions.length > 0 && (
                                            <div className="mb-3">
                                                <h6>Actions</h6>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {[...group.actions].sort().map((action, idx) => (
                                                        <Badge key={idx} bg="info">{action}</Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {renderGroupProperties(group)}

                                        {/* JSON Viewer Section */}
                                        <div className="mt-3">
                                            <Accordion>
                                                <Accordion.Item eventKey="json">
                                                    <Accordion.Header>
                                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                                        Raw JSON
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <ReactJson
                                                            src={group.raw_groups || [group]}
                                                            theme="ocean"
                                                            collapsed={1}
                                                            displayDataTypes={false}
                                                            displayObjectSize={false}
                                                            enableClipboard={true}
                                                            name={false}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </div>
                                    </Accordion.Body>

                                </Accordion.Item>
                            })}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>);
}

export default GroupCard;
